import React, { useState } from "react";
import { FiX, FiLock, FiEye, FiEyeOff, FiAlertTriangle, FiInfo, FiClock, FiRepeat } from "react-icons/fi";
import {
  CredentialAccessType,
  CredentialEntry,
  LinkVaultOptions,
  ReEncryptedEntry,
  Vault
} from "../../utils/api/endpoints/vault";
import {
  decryptCredentialUnified,
  decryptNotesUnified,
  encryptCredential,
  encryptNotes,
  DecryptedCredential,
  base64ToArrayBuffer
} from "../../utils/crypto/vault-crypto";

interface AccessPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (options: LinkVaultOptions) => Promise<void>;
  vault: Vault;
}

/**
 * Modal for setting up access password when linking a vault to a program.
 * Business owner must:
 * 1. Enter master password to decrypt entries
 * 2. Enter a new access password for sharing
 * 3. Choose access rules (unlimited, view_once, time_limited)
 */
const AccessPasswordModal: React.FC<AccessPasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vault
}) => {
  const [masterPassword, setMasterPassword] = useState("");
  const [accessPassword, setAccessPassword] = useState("");
  const [confirmAccessPassword, setConfirmAccessPassword] = useState("");
  // SECURITY: accessHint removed to prevent hint-based password guessing attacks
  const [accessType, setAccessType] = useState<CredentialAccessType>("unlimited");
  const [expiresAt, setExpiresAt] = useState("");
  const [showMasterPassword, setShowMasterPassword] = useState(false);
  const [showAccessPassword, setShowAccessPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1); // Step 1: Master password, Step 2: Access password

  if (!isOpen) return null;

  // Generate random salt as base64 string
  const generateSaltBase64 = (): string => {
    const salt = crypto.getRandomValues(new Uint8Array(32));
    let binary = "";
    for (let i = 0; i < salt.byteLength; i++) {
      binary += String.fromCharCode(salt[i]);
    }
    return btoa(binary);
  };

  const handleVerifyMasterPassword = async () => {
    setError(null);
    setIsProcessing(true);

    try {
      // Try to decrypt first entry to verify master password
      // Uses unified decryption that handles both encryption modes
      if (vault.entries && vault.entries.length > 0) {
        const entry = vault.entries[0];
        await decryptCredentialUnified(
          {
            entry_id: entry.entry_id,
            encrypted_data: entry.encrypted_data,
            iv: entry.iv,
            auth_tag: entry.auth_tag,
            salt: entry.salt,
            key_iterations: entry.key_iterations,
            encrypted_credential_key: entry.encrypted_credential_key
          },
          masterPassword,
          {
            encrypted_private_key: vault.encrypted_private_key,
            private_key_iv: vault.private_key_iv,
            private_key_salt: vault.private_key_salt,
            private_key_auth_tag: vault.private_key_auth_tag
          }
        );
      }
      setStep(2);
    } catch (err) {
      console.error("Master password verification failed:", err);
      setError("Invalid master password. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate access password
    if (accessPassword.length < 8) {
      setError("Access password must be at least 8 characters");
      return;
    }

    if (accessPassword !== confirmAccessPassword) {
      setError("Access passwords do not match");
      return;
    }

    if (accessType === "time_limited" && !expiresAt) {
      setError("Please select an expiration date");
      return;
    }

    setIsProcessing(true);

    try {
      // Generate salt for access password
      const accessSalt = generateSaltBase64();

      // Re-encrypt each entry with access password
      const reEncryptedEntries: ReEncryptedEntry[] = [];

      for (const entry of vault.entries || []) {
        // Decrypt with master password (handles both encryption modes)
        const decrypted: DecryptedCredential = await decryptCredentialUnified(
          {
            entry_id: entry.entry_id,
            encrypted_data: entry.encrypted_data,
            iv: entry.iv,
            auth_tag: entry.auth_tag,
            salt: entry.salt,
            key_iterations: entry.key_iterations,
            encrypted_credential_key: entry.encrypted_credential_key
          },
          masterPassword,
          {
            encrypted_private_key: vault.encrypted_private_key,
            private_key_iv: vault.private_key_iv,
            private_key_salt: vault.private_key_salt,
            private_key_auth_tag: vault.private_key_auth_tag
          }
        );

        // Re-encrypt with access password using the shared accessSalt
        const encrypted = await encryptCredential(decrypted, accessPassword, 600000, accessSalt);

        // Re-encrypt notes if present
        let reEncryptedNotes = {};
        if (entry.encrypted_notes) {
          try {
            // Decrypt notes with master password (handles both modes)
            const decryptedNotes = await decryptNotesUnified(
              {
                encrypted_notes: entry.encrypted_notes,
                notes_iv: entry.notes_iv,
                notes_auth_tag: entry.notes_auth_tag,
                salt: entry.salt,
                key_iterations: entry.key_iterations,
                encrypted_credential_key: entry.encrypted_credential_key
              },
              masterPassword,
              {
                encrypted_private_key: vault.encrypted_private_key,
                private_key_iv: vault.private_key_iv,
                private_key_salt: vault.private_key_salt,
                private_key_auth_tag: vault.private_key_auth_tag
              }
            );

            if (decryptedNotes) {
              // Re-encrypt with access password using the shared accessSalt
              const notesSaltArray = new Uint8Array(base64ToArrayBuffer(accessSalt));
              const encryptedNotes = await encryptNotes(decryptedNotes, accessPassword, notesSaltArray, 600000);
              if (encryptedNotes) {
                reEncryptedNotes = {
                  encrypted_notes: encryptedNotes.encrypted_notes,
                  notes_iv: encryptedNotes.notes_iv,
                  notes_auth_tag: encryptedNotes.notes_auth_tag
                };
              }
            }
          } catch (err) {
            console.error("Failed to re-encrypt notes for entry:", entry.entry_id, err);
          }
        }

        const reEncrypted: ReEncryptedEntry = {
          entry_id: entry.entry_id,
          encrypted_data: encrypted.encrypted_data,
          iv: encrypted.iv,
          auth_tag: encrypted.auth_tag,
          ...reEncryptedNotes
        };

        reEncryptedEntries.push(reEncrypted);
      }

      const options: LinkVaultOptions = {
        accessType,
        accessSalt,
        // SECURITY: accessHint removed to prevent hint-based password guessing attacks
        reEncryptedEntries,
        expiresAt: accessType === "time_limited" ? expiresAt : undefined,
        automatedAccessKey: accessPassword
      };

      await onSubmit(options);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to process credentials");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setMasterPassword("");
    setAccessPassword("");
    setConfirmAccessPassword("");
    setAccessType("unlimited");
    setExpiresAt("");
    setError(null);
    setStep(1);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Dark Header */}
        <div className="flex items-center justify-between p-4 bg-slate-800 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-700 rounded-lg">
              <FiLock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Link Credential</h2>
              <p className="text-xs text-slate-400">{vault.name}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <FiAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {step === 1 ? (
            <>
              {/* Step 1: Master Password */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <FiInfo className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Step 1: Verify Ownership</p>
                    <p className="text-xs mt-1 text-green-700">Enter your master password to decrypt the credentials.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Master Password
                </label>
                <div className="relative">
                  <input
                    type={showMasterPassword ? "text" : "password"}
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                    placeholder="Enter your master password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowMasterPassword(!showMasterPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showMasterPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Step 2: Access Password */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <FiInfo className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Step 2: Set Access Password</p>
                    <p className="text-xs mt-1 text-green-700">
                      Create a separate password for sharing. Share this with researchers,
                      NOT your master password.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Access Password
                </label>
                <div className="relative">
                  <input
                    type={showAccessPassword ? "text" : "password"}
                    value={accessPassword}
                    onChange={(e) => setAccessPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                    placeholder="Create access password (min 8 chars)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAccessPassword(!showAccessPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showAccessPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Access Password
                </label>
                <input
                  type="password"
                  value={confirmAccessPassword}
                  onChange={(e) => setConfirmAccessPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                  placeholder="Confirm access password"
                />
              </div>

              {/* SECURITY: Password hint input removed to prevent hint-based password guessing attacks */}

              {/* Access Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Type
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${accessType === "unlimited" ? "border-green-500 bg-green-50" : "border-gray-200 hover:bg-gray-50"}`}>
                    <input
                      type="radio"
                      name="accessType"
                      value="unlimited"
                      checked={accessType === "unlimited"}
                      onChange={() => setAccessType("unlimited")}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div className="p-2 rounded-lg bg-slate-700">
                      <FiRepeat className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Unlimited</p>
                      <p className="text-xs text-gray-500">Researchers can view anytime</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${accessType === "view_once" ? "border-green-500 bg-green-50" : "border-gray-200 hover:bg-gray-50"}`}>
                    <input
                      type="radio"
                      name="accessType"
                      value="view_once"
                      checked={accessType === "view_once"}
                      onChange={() => setAccessType("view_once")}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div className="p-2 rounded-lg bg-slate-700">
                      <FiEye className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">View Once</p>
                      <p className="text-xs text-gray-500">Each user can view only once</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${accessType === "time_limited" ? "border-green-500 bg-green-50" : "border-gray-200 hover:bg-gray-50"}`}>
                    <input
                      type="radio"
                      name="accessType"
                      value="time_limited"
                      checked={accessType === "time_limited"}
                      onChange={() => setAccessType("time_limited")}
                      className="text-green-600 focus:ring-green-500"
                    />
                    <div className="p-2 rounded-lg bg-slate-700">
                      <FiClock className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Time Limited</p>
                      <p className="text-xs text-gray-500">Access expires after a date</p>
                    </div>
                  </label>
                </div>
              </div>

              {accessType === "time_limited" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Expiration Date
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:bg-white"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-teal-50 rounded-b-2xl">
          <button
            onClick={step === 1 ? handleClose : () => setStep(1)}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-white transition-colors"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={step === 1 ? handleVerifyMasterPassword : handleSubmit}
            disabled={isProcessing || (step === 1 && !masterPassword) || (step === 2 && (!accessPassword || !confirmAccessPassword))}
            className="px-5 py-2.5 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isProcessing && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {step === 1 ? "Verify & Continue" : "Link Credential"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessPasswordModal;
